import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { TYPE } from 'theme';
import { Card } from '../styled';
import NFTCard from './components/NFTCard';
import { getOnePieceServerApi } from 'gql-ops';
import user from 'stores/user';
import { CircularProgress, IconButton } from '@mui/material';
import { ReloadIcon } from 'components/Icons';

const Wrapper = styled(Card)`
    padding: '30px 40px';
    margin-bottom: 40px;
    background: #000000;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        padding: 20px;
        margin-bottom: 20px;
    `}
    ${({ theme }) => theme.mediaWidth.upToExtraSmall`
        padding: 10px;
        padding-top: 20px;
    `}
`;

const NFTGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
    margin-top: 20px;
    max-width: 100%;

    & > * {
        width: 100%;
        min-width: 0;
    }

    @media (min-width: 1200px) {
        grid-template-columns: repeat(4, 1fr);
    }

    ${({ theme }) => theme.mediaWidth.upToSmall`
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
    `}

    ${({ theme }) => theme.mediaWidth.upToExtraSmall`
        grid-template-columns: 1fr;
    `}
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const EmptyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    gap: 16px;
`;

const HeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const TitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const CapsuleManagement = () => {
    const [nfts, setNfts] = useState<
        Array<{
            id: string;
            name: string;
            number: string;
            image: string | undefined;
        }>
    >([]);
    const [loading, setLoading] = useState(false);

    const fetchCapsulePieces = async () => {
        if (!user.address) return;

        setLoading(true);
        try {
            const res = await getOnePieceServerApi().userCapsulePieces({
                address: user.address,
            });

            if (res.userCapsulePieces) {
                const formattedNFTs = res.userCapsulePieces.map((piece) => ({
                    id: piece.tokenId,
                    name: piece.tokenName,
                    number: `#${piece.tokenId}`,
                    image: piece.tokenImageUrl,
                }));
                setNfts(formattedNFTs);
            }
        } catch (error) {
            console.error('Failed to fetch capsule pieces:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCapsulePieces();
    }, [user.address]);

    const renderContent = () => {
        if (loading) {
            return (
                <LoadingWrapper>
                    <CircularProgress />
                </LoadingWrapper>
            );
        }

        if (!nfts.length) {
            return (
                <EmptyWrapper>
                    <TYPE.desc fontSize={16}>No Capsule Pieces Found</TYPE.desc>
                    <TYPE.desc fontSize={14} color="#666">
                        You haven&apos;t collected any capsule pieces yet.
                    </TYPE.desc>
                </EmptyWrapper>
            );
        }

        return (
            <NFTGrid>
                {nfts.map((nft) => (
                    <NFTCard key={nft.id} {...nft} />
                ))}
            </NFTGrid>
        );
    };

    return (
        <Wrapper>
            <HeaderWrapper>
                <TitleWrapper>
                    <TYPE.main fontSize={24} marginBottom="10px" fontWeight={700}>
                        Capsule NFT Piece
                    </TYPE.main>
                    <TYPE.desc>Description: Capsule NFT Piece is community-driven NFT.</TYPE.desc>
                </TitleWrapper>
                <IconButton
                    onClick={fetchCapsulePieces}
                    disabled={loading}
                    sx={{
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                    }}
                >
                    <ReloadIcon type="outline" spin={loading} size={24} />
                </IconButton>
            </HeaderWrapper>
            {renderContent()}
        </Wrapper>
    );
};

export default CapsuleManagement;
